import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Card, Row, Col, Typography, Button, Input, DatePicker, 
  Table, Modal, Tag, Slider, Space, Statistic 
} from 'antd';
import { SearchOutlined, EyeOutlined, CommentOutlined } from '@ant-design/icons';
import { ResetIcon } from '../../components/common/ActionIcons';
import { listFeedbacks } from '../../actions/feedbackActions';
import PageWrapper from '../../components/common/PageWrapper';
import ReactECharts from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { PieChart, BarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  ToolboxComponent,
} from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import { ThemeContext } from '../../index';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const { Paragraph } = Typography;
const { RangePicker } = DatePicker;

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  PieChart,
  BarChart,
  SVGRenderer,
  ToolboxComponent
]);

const FeedbackManagementScreen = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { themeConfig } = useContext(ThemeContext);
  const isDarkMode = themeConfig.algorithm === 'dark';

  const [filter, setFilter] = useState({
    pageNumber: 1,
    pageSize: 10,
    filter: '',
    query: '',
    startDate: null,
    endDate: null,
    includeStatistics: true
  });

  const [searchUsername, setSearchUsername] = useState('');
  const [searchPage, setSearchPage] = useState('');
  const [ratingRange, setRatingRange] = useState([0, 10]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const feedbackList = useSelector((state) => state.feedbackList);
  const { loading, error, feedbacks, totalCount, statistics, currentPage, totalPages } = feedbackList;

  useEffect(() => {
    dispatch(listFeedbacks(filter));
  }, [dispatch, filter]);

  const handleSearch = () => {
    let newFilter = { ...filter, pageNumber: 1 };
    
    if (searchUsername.trim()) {
      newFilter = { ...newFilter, filter: 'username', query: searchUsername.trim() };
    } else if (searchPage.trim()) {
      newFilter = { ...newFilter, filter: 'page', query: searchPage.trim() };
    } else if (ratingRange[0] !== 0 || ratingRange[1] !== 10) {
      newFilter = { ...newFilter, filter: 'rating', query: `${ratingRange[0]}-${ratingRange[1]}` };
    } else {
      newFilter = { ...newFilter, filter: '', query: '' };
    }

    setFilter(newFilter);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleReset = () => {
    setSearchUsername('');
    setSearchPage('');
    setRatingRange([0, 10]);
    setFilter({
      pageNumber: 1,
      pageSize: 10,
      filter: '',
      query: '',
      startDate: null,
      endDate: null,
      includeStatistics: true
    });
  };

  const handleDateRangeChange = (dates) => {
    setFilter(prev => ({
      ...prev,
      startDate: dates?.[0]?.startOf('day').toISOString() || null,
      endDate: dates?.[1]?.endOf('day').toISOString() || null,
      pageNumber: 1
    }));
  };

  const showModal = (feedback) => {
    setSelectedFeedback(feedback);
    setIsModalVisible(true);
  };

  // Chart colors
  const colors = isDarkMode ? 
    ['#177ddc', '#49aa19', '#d89614', '#642ab5'] :
    ['#1890ff', '#52c41a', '#faad14', '#722ed1'];

  const getRatingDistributionOptions = () => {
    if (!statistics?.ratingDistribution) return {};

    const data = Object.entries(statistics.ratingDistribution)
      .map(([rating, count]) => ({
        value: count,
        name: `${rating}/10`,
        itemStyle: { 
          color: rating >= 7 ? colors[1] : 
                 rating >= 4 ? colors[2] : colors[0]
        }
      }));

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          return `${params.name}<br/>${t('Count')}: ${params.value} (${params.percent}%)`;
        }
      },
      legend: {
        orient: 'horizontal',
        bottom: 10,
        textStyle: { 
          color: '#999',
          fontSize: 11
        }
      },
      grid: {
        containLabel: true
      },
      series: [{
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: true,
        label: {
          show: true,
          formatter: '{b}: {c}',
          color: '#999',
          fontSize: 11
        },
        labelLine: {
          lineStyle: {
            color: '#999'
          }
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 12,
            color: '#999'
          }
        },
        data: data
      }]
    };
  };

  const getPageStatisticsOptions = () => {
    if (!statistics?.pageStatistics) return {};

    const data = statistics.pageStatistics
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
      .map(stat => {
        // Simplify page URLs for display
        let displayName = stat.page;
        if (displayName.startsWith('/session/')) {
          displayName = '/session';
        } else if (displayName.length > 15) {
          displayName = displayName.substring(0, 15) + '...';
        }

        return {
          name: displayName,
          fullPath: stat.page, // Keep full path for tooltip
          value: stat.averageRating,
          itemStyle: { 
            color: stat.averageRating >= 7 ? colors[1] : 
                   stat.averageRating >= 4 ? colors[2] : colors[0]
          }
        };
      });

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: function(params) {
          const data = params[0];
          return `${data.data.fullPath}<br/>
                  ${t('Average Rating')}: ${data.data.value.toFixed(1)}/10`;
        }
      },
      grid: {
        left: '5%',
        right: '5%',
        bottom: '20%',
        top: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: data.map(item => item.name),
        axisLabel: {
          interval: 0,
          rotate: 45,
          color: '#999',
          fontSize: 11,
          overflow: 'break'
        },
        axisLine: {
          lineStyle: {
            color: '#999'
          }
        }
      },
      yAxis: {
        type: 'value',
        max: 10,
        axisLabel: { 
          color: '#999',
          formatter: '{value}/10'
        },
        splitLine: {
          lineStyle: {
            color: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
          }
        },
        axisLine: {
          lineStyle: {
            color: '#999'
          }
        }
      },
      series: [{
        type: 'bar',
        data: data,
        barWidth: '40%',
        label: {
          show: true,
          position: 'top',
          formatter: '{c}',
          color: '#999',
          fontSize: 12
        }
      }]
    };
  };

  const columns = [
    {
      title: t('User Name'),
      dataIndex: 'userName',
      sorter: true,
      width: 150
    },
    {
      title: t('Rating'),
      dataIndex: 'rating',
      sorter: true,
      width: 100,
      render: (rating) => (
        <Tag color={rating >= 7 ? 'success' : rating >= 4 ? 'warning' : 'error'}>
          {rating}/10
        </Tag>
      )
    },
    {
      title: t('Message'),
      dataIndex: 'message',
      ellipsis: true,
      render: (text, record) => (
        <Space>
          <span>{text?.length > 50 ? `${text.substring(0, 50)}...` : text}</span>
          <Button 
            type="link" 
            icon={<EyeOutlined />} 
            onClick={() => showModal(record)}
          />
        </Space>
      )
    },
    {
      title: t('Page'),
      dataIndex: 'page',
      ellipsis: true,
      width: 200
    },
    {
      title: t('Contact Email'),
      dataIndex: 'contactEmail',
      ellipsis: true,
      width: 200
    },
    {
      title: t('Created At'),
      dataIndex: 'createdAt',
      sorter: true,
      width: 150,
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
    }
  ];

  return (
    <PageWrapper
      title={t('Feedback Management')}
      icon={<CommentOutlined style={{ fontSize: 24 }} />}
      showCard={false}
    >

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('Average Rating')}
              value={statistics?.meanRating?.toFixed(1) || 0}
              suffix="/10"
              valueStyle={{ 
                color: (statistics?.meanRating || 0) >= 7 ? '#52c41a' : 
                       (statistics?.meanRating || 0) >= 4 ? '#faad14' : '#f5222d'
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('Total Feedback')}
              value={statistics?.totalFeedback || 0}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('Total Pages')}
              value={statistics?.pageStatistics?.length || 0}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        <Col xs={24} lg={16}>
          <Card>
            <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
              <Col xs={24} sm={6}>
                <Input
                  placeholder={t('Search by username')}
                  value={searchUsername}
                  onChange={(e) => setSearchUsername(e.target.value)}
                  onKeyPress={handleKeyPress}
                  prefix={<SearchOutlined />}
                />
              </Col>
              <Col xs={24} sm={6}>
                <Input
                  placeholder={t('Search by page')}
                  value={searchPage}
                  onChange={(e) => setSearchPage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  prefix={<SearchOutlined />}
                />
              </Col>
              <Col xs={24} sm={6}>
                <RangePicker
                  onChange={handleDateRangeChange}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={6}>
                <Space>
                  <Button type="primary" onClick={handleSearch}>
                    {t('Search')}
                  </Button>
                  <Button icon={<ResetIcon />} onClick={handleReset}>
                    {t('Reset')}
                  </Button>
                </Space>
              </Col>
            </Row>

            <Row style={{ marginBottom: '16px' }}>
              <Col span={24}>
                <Paragraph>{t('Rating Range')}:</Paragraph>
                <Slider
                  range
                  min={0}
                  max={10}
                  value={ratingRange}
                  onChange={setRatingRange}
                  onAfterChange={handleSearch}
                  marks={{
                    0: '0',
                    2: '2',
                    4: '4',
                    6: '6',
                    8: '8',
                    10: '10'
                  }}
                />
              </Col>
            </Row>

            <Table
              columns={columns}
              dataSource={feedbacks}
              rowKey="id"
              loading={loading}
              pagination={{
                current: currentPage,
                pageSize: filter.pageSize,
                total: totalCount,
                showSizeChanger: true,
                showTotal: (total) => t('Total {{total}} items', { total }),
                onChange: (page, pageSize) => {
                  const newFilter = {
                    ...filter,
                    pageNumber: page,
                    pageSize
                  };
                  setFilter(newFilter);
                  dispatch(listFeedbacks(newFilter));
                }
              }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Row gutter={[0, 16]}>
            <Col span={24}>
              <Card title={t('Rating Distribution')}>
                <ReactECharts
                  echarts={echarts}
                  option={getRatingDistributionOptions()}
                  style={{ height: 300 }}
                />
              </Card>
            </Col>
            <Col span={24}>
              <Card title={t('Page Statistics')}>
                <ReactECharts
                  echarts={echarts}
                  option={getPageStatisticsOptions()}
                  style={{ height: 400 }}
                />
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      <Modal
        title={t('Feedback Details')}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            {t('Close')}
          </Button>
        ]}
        width={700}
      >
        {selectedFeedback && (
          <div>
            <Paragraph>
              <strong>{t('Rating')}:</strong>{' '}
              <Tag color={
                selectedFeedback.rating >= 7 ? 'success' : 
                selectedFeedback.rating >= 4 ? 'warning' : 'error'
              }>
                {selectedFeedback.rating}/10
              </Tag>
            </Paragraph>
            <Paragraph>
              <strong>{t('User Name')}:</strong> {selectedFeedback.userName}
            </Paragraph>
            <Paragraph>
              <strong>{t('Page')}:</strong> {selectedFeedback.page}
            </Paragraph>
            <Paragraph>
              <strong>{t('Contact Email')}:</strong> {selectedFeedback.contactEmail}
            </Paragraph>
            <Paragraph>
              <strong>{t('Created At')}:</strong> {dayjs(selectedFeedback.createdAt).format('YYYY-MM-DD HH:mm:ss')}
            </Paragraph>
            <Paragraph>
              <strong>{t('Message')}:</strong>
            </Paragraph>
            <div style={{ 
              whiteSpace: 'pre-wrap',
              padding: '16px',
              borderRadius: '4px'
            }}>
              {selectedFeedback.message}
            </div>
          </div>
        )}
      </Modal>
    </PageWrapper>
  );
};

export default FeedbackManagementScreen; 